import {
  Clerk,
  isClerkAPIResponseError,
  isUserLockedError,
} from '@clerk/clerk-js';
import { enUS, jaJP } from '@clerk/localizations';
import sharedMessages from '@/lib/shared-messages';
import { defineMessages } from './i18n';
import store from '@/store';
import {
  readIsLoggedIn,
  readUserLocale,
  readInitialized,
} from '@/store/main/getters';
import { commitAddNotification } from '@/store/main/mutations';
import { i18n } from '@/plugins/vue-i18n';

const m = defineMessages({
  invalidCode: '無効なコードです。もう一度お試しください。',
  tooManyRequests:
    '認証の試行回数が上限に達しました。10分後に再度お試しください。繰り返し発生する場合は、サポートまでご連絡ください。',
  tooManyFailedAttempts:
    '認証コードを複数回間違って入力されたため、現在の手続きは無効となりました。このページを閉じて、再度ログインをやり直してください。それでも解決しない場合は、サポートまでご連絡ください',
  expiredCode:
    'コードの有効期限が切れました（コードは10分間有効です）。もう一度お試しください。',
  emailNotFoundOrWrongPassword:
    'メールアドレスまたはパスワードが間違っています。',
  accountLocked:
    '一定回数連続でログインに失敗したため、セキュリティ保護のためお客様のアカウントをロックいたしましした。しばらく時間を置いてから再度ログインをしてください。',
  phoneNumberExists: 'この電話番号はすでに登録されています。',
  reverificationRequired:
    'セッション認証の有効期限が切れました。もう一度手続きをやり直してください。',
  verificationSmsRequestedTooManyTimes:
    '認証コードのリクエストが多すぎます。再度リクエストする前に、少なくとも30秒お待ちください。',
  loginSuccess: 'ログインに成功しました。引き続きケミカンをご利用ください。',
  enterpriseSSOFaild:
    '入力されたEmailアドレスは、SAMLログインの設定がされていません。通常のログイン画面よりログインをお願い致します。',
  samlUserNotRegistered:
    'このメールアドレスは登録されていません。管理者にユーザー登録を依頼してください。',
  samlLoginRequired:
    'このメールアドレスはSAMLログインが必要です。ログイン画面からSAMLログインを選択してください。',
});

type AuthIpNotAllowedFlash = {
  message: string;
  status: 'error';
};

if (!process.env.VUE_APP_CLERK_PUBLISHABLE_KEY) {
  throw new Error('Clerk publishable key is not set');
}

const publishableKey = process.env.VUE_APP_CLERK_PUBLISHABLE_KEY;

const AUTH_IP_NOT_ALLOWED_KEY = 'auth:ip-not-allowed';

function pullAuthIpNotAllowedFlash(): AuthIpNotAllowedFlash | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(AUTH_IP_NOT_ALLOWED_KEY);
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as AuthIpNotAllowedFlash;
    sessionStorage.removeItem(AUTH_IP_NOT_ALLOWED_KEY);
    return data;
  } catch {
    sessionStorage.removeItem(AUTH_IP_NOT_ALLOWED_KEY);
    return null;
  }
}

const clerk: Clerk = new Clerk(publishableKey);

const isClerkReady = new Promise((resolve) => {
  clerk
    .load({
      allowedRedirectOrigins: [
        process.env.VUE_APP_RISK_ASSESSMENT_URL || 'http://localhost:3000',
      ],
    })
    .then(() => {
      const ipNotAllowedFlash = pullAuthIpNotAllowedFlash();
      if (ipNotAllowedFlash) {
        commitAddNotification(store, {
          messageKey: sharedMessages.noPermission,
          messageValues: { details: ipNotAllowedFlash.message },
          color: ipNotAllowedFlash.status ?? 'error',
        });
      }

      resolve(true);
    });
});

let sessionEnded = false;
clerk.addListener((resources) => {
  if (!resources.session && !resources.user && !sessionEnded) {
    // If the session has ended, and we were logged in, set the login state
    // so that we can show the appropriate message on the login screen.
    if (readIsLoggedIn(store)) {
      sessionEnded = true;
      sessionStorage.setItem(
        'loginState',
        JSON.stringify({ tokenInvalidated: true })
      );
    }
  }
  if (
    readInitialized(store) &&
    !readIsLoggedIn(store) &&
    resources.session &&
    resources.session?.status === 'active'
  ) {
    // The user has just signed in, so we show the login success message.
    commitAddNotification(store, {
      messageKey: m.loginSuccess,
      color: 'success',
    });
  }
});

const getClerkErrorCode = (error: Error | undefined): string => {
  if (isClerkAPIResponseError(error)) {
    return error.errors[0].code;
  }
  return 'unknown_error';
};

const getMessageKeyByClerkError = (error: Error | undefined): string => {
  const $t = i18n.tc.bind(i18n);
  const locale = readUserLocale(store);
  const clerkLocale = locale === 'ja-JP' ? jaJP : enUS;

  // Check if error message contains SAML-related text (for string errors)
  const errorMessage = error?.message || '';
  if (
    errorMessage.includes('password') &&
    errorMessage.includes('is not allowed') &&
    errorMessage.includes('SAML')
  ) {
    return $t(m.samlLoginRequired);
  }

  if (isUserLockedError(error)) {
    return $t(m.accountLocked);
  }
  if (isClerkAPIResponseError(error)) {
    // Check if the error message contains SAML-related text
    const clerkErrorMessage = error.errors[0].message || '';
    if (
      clerkErrorMessage.includes('password') &&
      clerkErrorMessage.includes('is not allowed') &&
      clerkErrorMessage.includes('SAML')
    ) {
      return $t(m.samlLoginRequired);
    }

    // Possible errors can be found here:
    // https://clerk.com/docs/errors/overview
    switch (error.errors[0].code) {
      case 'form_code_incorrect':
        return $t(m.invalidCode);
      case 'too_many_requests':
        return $t(m.tooManyRequests);
      case 'verification_expired':
        return $t(m.expiredCode);
      case 'verification_failed':
        return $t(m.tooManyFailedAttempts);
      case 'session_reverification_required':
        return $t(m.reverificationRequired);
      case 'verification_code_too_many_requests':
        return $t(m.verificationSmsRequestedTooManyTimes);
      case 'form_identifier_not_found':
      case 'form_password_incorrect':
        return $t(m.emailNotFoundOrWrongPassword);
      case 'form_param_format_invalid':
        return $t(sharedMessages.invalidEmail);
      case 'form_identifier_exists':
        return $t(m.phoneNumberExists);
      case 'enterprise_sso_additional_identifications_disabled':
      case 'enterprise_sso_email_address_domain_mismatch':
        return $t(m.enterpriseSSOFaild);
      case 'enterprise_sso_sign_in_connection_missing':
      case 'enterprise_sso_sign_up_connection_missing':
        // This typically means the user is not registered in the system
        // (when JIT Provisioning is OFF)
        return $t(m.samlUserNotRegistered);
      case 'form_password_not_allowed_for_saml':
      case 'strategy_for_user_invalid':
        return $t(m.samlLoginRequired);
      default:
        return (
          // Falling back to community translated messages.
          // These are however potentially unstable and can change.
          (clerkLocale.unstable__errors as Record<string, string | undefined>)[
            error.errors[0].code
          ] || $t(sharedMessages.genericError)
        );
    }
  }
  return $t(sharedMessages.genericError);
};

export {
  clerk as default,
  isClerkReady,
  getMessageKeyByClerkError,
  getClerkErrorCode,
};
